import React, { useMemo, useCallback } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Paper,
  TextField,
  IconButton,
  Autocomplete,
  CircularProgress,
} from '@mui/material';
import {
  Download as DownloadIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Construction as CementIcon,
} from '@mui/icons-material';

import { PropertyOption } from '../types/report.types';

import { CheckboxGroup } from './checkbox-group';
import { DateRangePicker } from './date-range-picker';

import { getOptionKey } from '@/utils/helpers';
import { commonButtonStyles } from '@/components/styles';

const MAX_ROWS = 10;

export interface ReportSectionCardProps {
  id: string;
  title: string;
  startDate: string;
  endDate: string;
  onStartDateChange: (date: string) => void;
  onEndDateChange: (date: string) => void;
  onOutput: () => void;

  deliveryStaffOptions?: Array<{ id: string; label: string }>;
  selectedDeliveryStaff?: string[];
  onDeliveryStaffChange?: (selectedIds: string[]) => void;

  desiredNames?: string[];
  onDesiredNamesChange?: (names: string[]) => void;

  propertyOptions?: PropertyOption[];
  propertyOptionsLoading?: boolean;

  outputButtonText?: string;
  showDragDrop?: boolean;
  onAddInputRow?: () => void;
  inputRowCount?: number;
  maxInputRows?: number;
  children?: React.ReactNode;
  selectedDepartmentCodes?: number[];
}

export const ReportSectionCard: React.FC<ReportSectionCardProps> = ({
  id,
  title,
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  onOutput,

  deliveryStaffOptions,
  selectedDeliveryStaff = [],
  onDeliveryStaffChange,

  desiredNames = [],
  onDesiredNamesChange,

  propertyOptions = [],
  propertyOptionsLoading = false,

  outputButtonText = '帳票出力',
  children,
  selectedDepartmentCodes = [],
}) => {
  // 1) Build lookup maps once (fast)
  const optionByKey = useMemo(() => {
    const map = new Map<string, PropertyOption>();
    for (const opt of propertyOptions) {
      if (opt.key) map.set(String(opt.key), opt);
      // backward compatibility lookups
      map.set(opt.value, opt);
      map.set(opt.label, opt);
    }
    return map;
  }, [propertyOptions]);

  const validPropertyKeys = useMemo(() => {
    return new Set(propertyOptions.map((option) => option.key));
  }, [propertyOptions]);

  const isInvalidDesiredName = useCallback(
    (value?: string) => Boolean(value && !validPropertyKeys.has(value)),
    [validPropertyKeys]
  );

  const hasInvalidDesiredNames = useMemo(
    () => desiredNames.some((value) => isInvalidDesiredName(value)),
    [desiredNames, isInvalidDesiredName]
  );

  const selectedPropertyOptions = useMemo(() => {
    return desiredNames.map((id) => {
      if (!id) return null;
      return optionByKey.get(id) ?? { key: id, value: id, label: id };
    });
  }, [desiredNames, optionByKey]);

  const isFormValid = useMemo(() => Boolean(startDate && endDate), [startDate, endDate]);

  // 2) Helpers for desiredNames updates (avoid repeating code)
  const updateDesiredNameAt = useCallback(
    (index: number, value: string) => {
      if (!onDesiredNamesChange) return;
      const next = [...desiredNames];
      next[index] = value;
      onDesiredNamesChange(next);
    },
    [desiredNames, onDesiredNamesChange]
  );

  const removeDesiredNameAt = useCallback(
    (index: number) => {
      if (!onDesiredNamesChange) return;
      onDesiredNamesChange(desiredNames.filter((_, i) => i !== index));
    },
    [desiredNames, onDesiredNamesChange]
  );

  const addDesiredNameRow = useCallback(() => {
    if (!onDesiredNamesChange) return;
    onDesiredNamesChange(desiredNames.length ? [...desiredNames, ''] : ['']);
  }, [desiredNames, onDesiredNamesChange]);

  const getOptionLabel = useCallback((option: PropertyOption | string) => {
    if (typeof option === 'string') return option;
    return option?.label ?? '';
  }, []);

  const isOptionEqualToValue = useCallback(
    (option: PropertyOption | string, value: PropertyOption | string) => {
      if (typeof option === 'string' || typeof value === 'string') return option === value;
      return option?.key === value?.key;
    },
    []
  );

  const deliveryStaffContent = useMemo(() => {
    if (selectedDepartmentCodes.length === 0) {
      return (
        <Typography variant='body1' sx={{ fontWeight: 600, color: 'text.secondary' }}>
          部署を選択してください。
        </Typography>
      );
    }
    if (deliveryStaffOptions && deliveryStaffOptions.length > 0) {
      return (
        <Box sx={{ mt: 3 }}>
          <CheckboxGroup
            title=''
            options={deliveryStaffOptions}
            selectedIds={selectedDeliveryStaff}
            onChange={onDeliveryStaffChange ?? (() => {})}
            columns={4}
            showSelectAll
          />
        </Box>
      );
    }
    return null;
  }, [
    selectedDepartmentCodes.length,
    deliveryStaffOptions,
    selectedDeliveryStaff,
    onDeliveryStaffChange,
  ]);

  return (
    <Card
      id={id}
      elevation={1}
      sx={{
        mb: 3,
        borderRadius: 2,
        '&:last-child': { mb: 0 },
      }}
    >
      <CardContent sx={{ p: 3, '&:last-child': { paddingBottom: '40px' } }}>
        <Typography
          variant='h5'
          sx={{
            mb: 3,
            color: '#1A1C20',
            fontSize: '22px',
            fontWeight: 600,
            textAlign: 'left',
          }}
        >
          {title}
        </Typography>

        {children && <Box sx={{ mb: 3 }}>{children}</Box>}

        {/* Delivery Staff Selection */}
        {deliveryStaffOptions && (
          <Typography variant='subtitle1' fontWeight={600} sx={{ mb: 1.5 }}>
            デリバリー担当者
          </Typography>
        )}

        {deliveryStaffOptions && onDeliveryStaffChange && (
          <Box
            sx={{
              mb: 3,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              p: 3,
              ...(selectedDepartmentCodes.length === 0 && {
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                minHeight: '120px',
              }),
            }}
          >
            {deliveryStaffContent}
          </Box>
        )}

        {/* Desired Names */}
        {onDesiredNamesChange && (
          <Box sx={{ mb: 3 }}>
            <Box
              sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}
            >
              <Typography
                variant='subtitle1'
                fontWeight={600}
                sx={{ color: '#1A1C20', fontSize: '16px' }}
              >
                現場
              </Typography>
            </Box>

            {desiredNames.length === 0 ? (
              <Box
                sx={{
                  textAlign: 'center',
                  py: 4,
                  color: 'text.secondary',
                  border: '2px dashed',
                  borderColor: 'divider',
                  borderRadius: 1,
                }}
              >
                {propertyOptionsLoading ? (
                  <>
                    <CircularProgress sx={{ mb: 2, color: '#0047BA' }} />
                    <Typography variant='body1'>データを読み込み中...</Typography>
                  </>
                ) : (
                  <>
                    <CementIcon sx={{ fontSize: 48, mb: 1, color: '#C4C6D0' }} />
                    <Typography variant='body1'>現場名称</Typography>
                    <Button
                      variant='outlined'
                      startIcon={<AddIcon />}
                      onClick={addDesiredNameRow}
                      disabled={propertyOptionsLoading}
                      sx={{ ...commonButtonStyles, mt: 2, px: 3, py: 1 }}
                    >
                      入力行を追加（0/{MAX_ROWS}）
                    </Button>
                  </>
                )}
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {desiredNames.map((_, index) => (
                  <Paper
                    // NOTE: key-by-index is still not ideal, but keep behavior same.
                    // Best fix: switch desiredNames to [{rowId, value}] in parent state.
                    key={`${id}-desired-${index}`}
                    variant='outlined'
                    sx={{ p: 2 }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        mb: 2,
                      }}
                    >
                      <Typography variant='subtitle1' fontWeight={600}>
                        <CementIcon sx={{ fontSize: 24, mr: 1, color: '#0047BA' }} />
                        現場名称
                      </Typography>

                      <IconButton
                        color='error'
                        onClick={() => removeDesiredNameAt(index)}
                        size='small'
                        sx={{ color: '#FF8985' }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>

                    <Autocomplete
                      options={propertyOptions}
                      getOptionKey={getOptionKey}
                      loading={propertyOptionsLoading}
                      value={selectedPropertyOptions[index] ?? null}
                      onInputChange={(_, newInputValue, reason) => {
                        if (reason === 'input') {
                          updateDesiredNameAt(index, newInputValue);
                        }
                      }}
                      onChange={(_, newValue) => {
                        if (typeof newValue === 'string') {
                          updateDesiredNameAt(index, newValue);
                        } else {
                          updateDesiredNameAt(index, newValue ? newValue.key : '');
                        }
                      }}
                      getOptionLabel={getOptionLabel}
                      isOptionEqualToValue={isOptionEqualToValue}
                      freeSolo
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label='現場名称'
                          placeholder='現場名称を入力'
                          error={isInvalidDesiredName(desiredNames[index])}
                          helperText={
                            isInvalidDesiredName(desiredNames[index])
                              ? '現場略称の入力はサジェストから選択ください。'
                              : undefined
                          }
                          slotProps={{
                            input: {
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {propertyOptionsLoading && <CircularProgress size={20} />}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            },
                          }}
                        />
                      )}
                      noOptionsText='データが見つかりません'
                    />
                  </Paper>
                ))}
              </Box>
            )}

            {desiredNames.length > 0 && desiredNames.length < MAX_ROWS && (
              <Box sx={{ textAlign: 'center', mt: 3 }}>
                <Button
                  variant='outlined'
                  startIcon={<AddIcon />}
                  onClick={addDesiredNameRow}
                  disabled={propertyOptionsLoading}
                  sx={{ ...commonButtonStyles, px: 3, py: 1 }}
                >
                  入力行を追加 ({desiredNames.length}/{MAX_ROWS})
                </Button>
              </Box>
            )}
          </Box>
        )}

        {/* Date Range */}
        <Box sx={{ mb: 3 }}>
          <DateRangePicker
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={onStartDateChange}
            onEndDateChange={onEndDateChange}
          />
        </Box>

        {/* Output Button */}
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Button
            variant='contained'
            startIcon={<DownloadIcon />}
            onClick={onOutput}
            disabled={!isFormValid || hasInvalidDesiredNames}
            sx={{
              ...commonButtonStyles,
              minWidth: '200px',
              backgroundColor: '#0047BA',
              '&:disabled': { backgroundColor: '#C4C6D0', color: '#FFFFFF' },
            }}
          >
            {outputButtonText}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};
